"use client";

import { useTranslations } from "next-intl";
import styled from "styled-components";
import { useBoundStore } from "@/lib/stores/useBoundStore";

const LegalContainer = styled.div<{ $onPage: boolean }>`
  height: fit-content;
  overflow: auto;
  max-height: 75vh;
  margin-bottom: 50px;
  padding: ${(props) => (props.$onPage ? "30px" : "10px")};

  h1 {
    margin: 0 0 20px 0;
  }

  h3 {
    margin: 20px 0 10px 0;
  }

  .underline {
    text-decoration: underline !important;
  }

  p span {
    color: ${(props) => props.theme.black};
    font-weight: bold;
    text-decoration: none !important;
  }

  li span {
    text-decoration: underline;
  }

  .center-align {
    text-align: center;
    width: 100%;
  }

  .upper {
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  ol,
  ul {
    color: ${(props) => props.theme.black};
    margin: 10px 0 0 35px;
    font-size: 0.95rem;

    li {
      margin-bottom: 5px;
    }
  }

  p {
    color: ${(props) => props.theme.black};
    font-size: 0.95rem;
  }

  a {
    color: ${(props) => props.theme.link};

    &:hover {
      color: ${(props) => props.theme.black};
    }
  }

  @media (max-width: 425px) {
    max-height: 92vh;
    padding: ${(props) => (props.$onPage ? "20px" : "0")};
    padding: 0px 15px;
    font-size: 0.75rem !important;

    p,
    ol,
    ul {
      font-size: 0.75rem !important;
    }
  }
`;

export function ANZTermsOfUseContent({
  onPage,
  onOpenPrivacy,
}: {
  onPage?: boolean;
  onOpenPrivacy?: () => void;
}) {
  const t = useTranslations("termsOfAcceptance.anzTermsOfUse");
  const setModalType = useBoundStore((state) => state.setModalType);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);

  // On the /legal page a caller supplies onOpenPrivacy to switch tabs; elsewhere
  // (e.g. AcceptTerms) the link opens the privacy modal as before.
  const openPrivacy = () => {
    if (onOpenPrivacy) {
      onOpenPrivacy();
      return;
    }
    setModalType("anz-privacy-policy");
    setModalOpen(true);
  };
  return (
    <LegalContainer $onPage={onPage || false}>
      <h1>{t("title")}</h1>
      <p style={{ fontStyle: "italic" }}>{t("lastUpdated")}</p>
      <br />
      <p>{t("intro")}</p>
      <br />
      <p>{t("intro2")}</p>
      <br />
      <p>
        {t.rich("intro3", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                style={{ textDecoration: "none", cursor: "pointer" }}
                onClick={openPrivacy}
              >
                <a>{text}</a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <p>{t("intro4")}</p>
      <br />
      <p>
        {t.rich("intro5", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section1Title")}</h3>
      <br />
      <p>
        {t.rich("section1Para1", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                style={{
                  fontStyle: "italic",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <p>{t("section1Para2")}</p>
      <br />
      <h3>{t("section2Title")}</h3>
      <br />
      <p>{t("section2Para1")}</p>
      <br />
      <p>{t("section2Para2")}</p>
      <br />
      <h3>{t("section3Title")}</h3>
      <br />
      <p>{t("section3Para")}</p>
      <br />
      <h3>{t("section4Title")}</h3>
      <br />
      <p>{t("section4Para")}</p>
      <br />
      <h3>{t("section5Title")}</h3>
      <br />
      <p>{t("section5Intro")}</p>
      <ul>
        <li>{t("section5UL1")}</li>
        <li>
          {t.rich("section5UL2", {
            link: (chunks) => {
              const text = chunks!.toString();
              return (
                <span
                  className="underline"
                  style={{
                    fontWeight: "normal",
                  }}
                >
                  {text}
                </span>
              );
            },
          })}
        </li>
        <li>{t("section5UL3")}</li>
        <li>{t("section5UL4")}</li>
        <li>{t("section5UL5")}</li>
        <li>{t("section5UL6")}</li>
        <li>{t("section5UL7")}</li>
        <li>{t("section5UL8")}</li>
        <li>{t("section5UL9")}</li>
        <li>{t("section5UL10")}</li>
        <li>{t("section5UL11")}</li>
        <li>{t("section5UL12")}</li>
        <li>{t("section5UL13")}</li>
        <li>{t("section5UL14")}</li>
      </ul>
      <p>{t("section5Para")}</p>
      <br />
      <h3>{t("section6Title")}</h3>
      <br />
      <p>
        {t.rich("section6Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section7Title")}</h3>
      <br />
      <p>{t("section7Para")}</p>
      <br />
      <h3>{t("section8Title")}</h3>
      <br />
      <p>{t("section8Para1")}</p>
      <br />
      <p>{t("section8Para2")}</p>
      <br />
      <p>{t("section8Para3")}</p>
      <br />
      <h3>{t("section9Title")}</h3>
      <br />
      <p>
        {t.rich("section9Para1", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <p>{t("section9Para2")}</p>
      <br />
      <h3>{t("section10Title")}</h3>
      <br />
      <p>
        {t.rich("section10Para1", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <p>
        {t.rich("section10Para2", {
          link: (chunks) => (
            <span style={{ textDecoration: "none", fontWeight: "bold" }}>
              {chunks}
            </span>
          ),
        })}
      </p>
      <br />
      <p>
        {t.rich("section10Para3", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <p>{t("section10Para4")}</p>
      <br />
      <p>{t("section10Para5")}</p>
      <br />
      <h3>{t("section11Title")}</h3>
      <br />
      <p>{t("section11Para")}</p>
      <br />
      <h3>{t("section12Title")}</h3>
      <br />
      <p>{t("section12Para")}</p>
      <br />
      <h3>{t("section13Title")}</h3>
      <br />
      <p>
        {t.rich("section13Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section14Title")}</h3>
      <br />
      <p>
        {t.rich("section14Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section15Title")}</h3>
      <br />
      <p>{t("section15Para1")}</p>
      <br />
      <p>{t("section15Para2")}</p>
      <br />
      <p>{t("section15Para3")}</p>
      <br />
      <p>{t("section15Para4")}</p>
      <br />
      <p>
        {t.rich("section15Para5", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <p>
        {t.rich("section15Para6", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span style={{ textDecoration: "none" }}>
                <a href="mailto:VPPANZHelpDesk@its.jnj.com">{text}</a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section16Title")}</h3>
      <br />
      <p>{t("section16Para1")}</p>
      <br />
      <p>{t("section16Para2")}</p>
      <br />
      <p>
        {t.rich("section16Para3", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
      <br />
      <p>{t("section16Para4")}</p>
      <br />
      <p>{t("section16Para5")}</p>
      <br />
      <p>{t("section16Para6")}</p>
      <br />
      <p>{t("section16Para7")}</p>
      <br />
      <h3>{t("section17Title")}</h3>
      <br />
      <p>{t("section17Para")}</p>
      <br />
      <h3>{t("section18Title")}</h3>
      <br />
      <p>{t("section18Para")}</p>
      <br />
      <h3>{t("section19Title")}</h3>
      <br />
      <p>{t("section19Para")}</p>
      <br />
      <h3>{t("section20Title")}</h3>
      <br />
      <p>
        {t.rich("section20Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span style={{ textDecoration: "none" }}>
                <a href={text} target="_blank" rel="noopener noreferrer">
                  {text}
                </a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section21Title")}</h3>
      <br />
      <p>
        {t.rich("section21Para1", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span style={{ textDecoration: "none" }}>
                <a href="mailto:VPPANZHelpDesk@its.jnj.com">{text}</a>
              </span>
            );
          },
        })}
      </p>
      <p style={{ fontWeight: "bold", marginTop: "10px" }}>
        {t("section21Address1")}
      </p>
      <p style={{ fontWeight: "bold" }}>{t("section21Address2")}</p>
      <p style={{ fontWeight: "bold" }}>{t("section21Address3")}</p>
      <p style={{ fontWeight: "bold" }}>{t("section21Address4")}</p>
      <br />
      <p>
        {t.rich("section21Para2", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span style={{ textDecoration: "none" }}>
                <a href="mailto:VPPANZHelpDesk@its.jnj.com">{text}</a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section22Title")}</h3>
      <br />
      <p>
        {t.rich("section22Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span style={{ textDecoration: "none" }}>
                <a href={text} target="_blank" rel="noopener noreferrer">
                  {text}
                </a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section23Title")}</h3>
      <br />
      <p>
        {t.rich("section23Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (!text.startsWith("www")) {
              return (
                <span
                  className="underline"
                  style={{
                    fontWeight: "normal",
                  }}
                >
                  {text}
                </span>
              );
            }
            return (
              <span style={{ textDecoration: "none" }}>
                <a
                  href={`https://${text}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section24Title")}</h3>
      <br />
      <p>{t("section24Para")}</p>
      <br />
      <h3>{t("section25Title")}</h3>
      <br />
      <p>
        {t.rich("section25Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                className="underline"
                style={{
                  fontWeight: "normal",
                }}
              >
                {text}
              </span>
            );
          },
        })}
      </p>
    </LegalContainer>
  );
}
